import {ICatalogItemDetailsPathElement, openInNewTab} from '@modules'
import {WorkspaceId, site, WorkspaceConfig, deployments, prettyPrint, CatalogQueryParams, globalStores as stores, CatalogStore, globalStores} from '@stores'
import {slugify, _, match, action, computed, observable, lockr, jsonComputedOptions, jsondiff, uiConstants, RouteComponentProps} from '@api'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {authStore} from '@utils'
import {autorun, runInAction} from 'mobx'
import utils from '@modules/common/utils'

const DEBUG_NAV = false && DEBUG

export const slugs = {
  dashboard: 'vip',
  itemDetails: 'item-details'
}

/**
 * All available/possible query parameters that could show up across the site - usually distinguished by route (ie  /vip vs /item-details)
 */
export type SiteQueryParams = DashboardQueryParams | CatalogItemPageQueryParams

export interface CommonQueryParams {
  deployment?: string
  devLogin?: string

  /**
   * Mobx Debugging
   */
  mobx?: null | undefined
}

export interface CatalogItemPageQueryParams extends CommonQueryParams {
  itemId?: string
  ws?: string
  path: string
}

export type DashboardQueryParams = CatalogQueryParams & {
  /**
   * Corresponds to a vip-reports._id
   */
  id?: string // Short url in vlp-urls

  /**
   * Custom URL/slug user can set instead of using id
   */
  report?: string

  search?: string | undefined
  qr?: null | undefined
}

export class NavStore {
  @observable location: { search, pathname: string, state? }

  @computed get kibanaUrl() {
    const {config: {config: {esUrl, esApiKey}}, es: {isCloudES}} = stores
    return isCloudES
           // In ES cloud kibana is of the form:  https://rh-uat.kb.us-east4.gcp.elastic-cloud.com:9243/app/home#/
           ? `${_.trimEnd(esUrl.replace(/(.*?)(\.es)(\..*)/, (match, p1, p2, p3) => `${p1}.kb${p3}`))}/app/dev_tools#/console`
           : `${_.trimEnd(deployments.selectedDeployment.url, '/')}/kibana/app/kibana#/dev_tools/console?_g=()`
  }

  @computed.struct get params(): { season?, category1?, category2? } {
    return this.match ? this.match.params : {}
  }

  constructor() {
    const {location: {search, pathname}} = window
    this.location = {search, pathname}

    // synchronizeStores(() => {
    //   DEBUG && autorun(() => {
    //     const {stores: {ui}} = this
    //     runInAction(() => {
    //       if (DEBUG) {
    //         _.merge(ui.settings, {dev: {virtualizedCatalog: virtualized === null, localNest: localNest === null, localSAM: localSAM === null}})
    //       }
    //       // console.log({localNest, useLocalNest: ui.settings.useLocalNest})
    //     })
    //   })
    // })
  }

  /**
   * Never directly modify this - call updateQueryParams instead
   */
  @computed(jsonComputedOptions) get queryParams(): SiteQueryParams {
    return utils.parseQs(this.location ? this.location.search : window.location.search)
  }

  get dashboardQueryParams() : DashboardQueryParams {
    return this.queryParams
  }

  // This method useful to check whether seasonal tab view is active or not
  @computed get isSeasonalLibraryView() {
    return false
    //return ((this.location.pathname.indexOf('seasonalLibrary') !== -1) || (nav.queryParams.libraryTab && nav.queryParams.libraryTab === 'seasonal-library'))
  }

  @computed get disableSpecActions() {
    return (!this.match.params.category2 && this.match.params.season)
  }

  // Only will have matches of whatever was handed to updateLocation()
  @observable match: match<{ season?, category1?, category2? }>
  private prevPathName: string = null
  updateLocation = (location: { search: string, pathname: string }, match: match<any> = null) => {
    let {search, pathname} = location
    // this.location.search will not have a ?
    if (search.startsWith('?')) {
      search = search.substring(1)
    }

    const locationChanged = this.location.pathname !== this.prevPathName
    if (this.location.search != search || locationChanged /*|| !_.isEqual(this.match, match)*/) {
      DEBUG_NAV && console.debug(`Nav location changed`, {search, match, path: location.pathname, previous: _.pick(this, ['prevPathName', 'location'])})
      runInAction(`Update URL to ${pathname}`, () => {
        this.location.search = search
        this.location.pathname = pathname
        this.prevPathName = pathname

        if (locationChanged || !this.match || !_.isEmpty(match.params)) {
          this.match = match
        }
      })
    }
  }

  get reactRouter(): Partial<RouteComponentProps> {
    return site.props
  }

  get history() { return site?.props?.history }  // Get ReactRouter passed props from react tree

  urlFor_string = (params: ({ workspace?: Partial<WorkspaceConfig>, queryParams?: DashboardQueryParams })) => {
    return this.urlString(this.urlFor(params))
  }

  urlFor = ({workspace, queryParams}: ({ workspace?: Partial<WorkspaceConfig>, queryParams?: SiteQueryParams })) => {
    return {
      pathname: `/${this.slugForWorkspace(workspace ?? stores.workspaces.currentConfig)}`,
      search: utils.stringifyQs(_.merge({}, this.defaultQueryParam, (!queryParams || _.isEmpty(queryParams)) ? undefined : queryParams))
    }
  }

  urlString = ({pathname, search}) => {
    return `${pathname}?${search}`
  }

  @action routeToDashboard() {
    this.history.push(`/${slugs.dashboard}`)
  }

  @action routeTo({workspace, queryParams, verb}: ({ workspace?: Partial<WorkspaceConfig>; queryParams?: DashboardQueryParams; verb?: 'replace' | 'push' })) {
    verb = verb || 'push'

    const {history} = this,
      {workspaces} = stores

    let url = this.urlFor({workspace: workspace, queryParams})

    //console.log(`routeTo:`, url)
    if (TEST) {
      console.warn(`System requested navigate to ${url}`)
      return
    }

    if (verb == 'push') {
      history.push(url)
      workspaces.current = workspace._id
    } else if (verb == 'replace') {
      history.replace(url)
      workspace && (workspaces.current = workspace?._id)
    } else {
      throw new Error(`Unknown verb '${verb}`)
    }

    this.location = url
  }

  @action pushRoute = (url: string, workspaceId?: WorkspaceId) => {
    this.history.push(url)
    workspaceId && (stores.workspaces.current = workspaceId)

    this.location = {pathname: url, search: undefined}
  }

  @action setQueryParams = (params: DashboardQueryParams) => {
    const {history} = this

    const search = utils.stringifyQs({...params})
    history.replace({search})
    // console.log(`setQueryParams`, params)
    this.location = {...this.location, search}
  }

  @action updateQueryParams = (params: DashboardQueryParams, historyAction: 'push' | 'replace' = 'replace') => {
    // console.log(`updateQueryParams`, params)
    const {history, queryParams} = this
    const search = utils.stringifyQs({...queryParams, ...params})
    if (!_.isEqual(utils.parseQuery(search), utils.parseQuery(location.search))) {
      historyAction == 'replace' ? history.replace({search}) : history.push({search})
    }
    if (this.location.search != search) {
      this.location.search = search
    } else {
      // DEBUG && console.debug(`Did not update query params as they did not actually change.`)
    }

  }

  @computed get deploymentQueryParam() {
    const {stores: {deployments: {selectedDeploymentId}}} = this
    return DEBUG && selectedDeploymentId ? `deployment=${selectedDeploymentId}` : ''
  }

  @action routeToLoginPage = () => {
    if (TEST) {
      console.warn('System requested navigate to /login')
      return
    }
    const {history, deploymentQueryParam} = this
    history.push(`/login${deploymentQueryParam ? `?${deploymentQueryParam}` : ''}`)
  }

  @action routeToLogoutPage = async () => {
    if (TEST) {
      console.warn('System requested navigate to /logout')
      return
    }

    await authStore.logout()
    // if (window.location.pathname != '/login' && window.location.pathname != '/logout' && !window.location.pathname.includes('/s/')) {
    //   window.location.pathname = '/logout'
    // }
  }

  @action siteHeader_navigateToWorkspace = (workspace: Partial<WorkspaceConfig>, perspective?: string, openNewTab?: boolean, queryParams: Partial<DashboardQueryParams> = {}) => {
    const {workspaces} = stores
    if (!openNewTab) {
      stores.filter.resetFilters()
      runInAction(`siteHeader_navigateToWorkspace(${prettyPrint({workspace, perspective})})`, () => {
        workspaces.current = workspace._id
        const {catalog} = workspaces.storesForWorkspace(workspaces.workspaceFor(workspace._id))
        catalog.switchingWorkspaces = true
        catalog.settings.perspective = perspective
        stores.catalog.groupBy.hierarchy = []
        stores.catalog.selection.clear()
        stores.sidebar.left.path = []
        nav.routeTo({workspace, queryParams: {...nav.queryParams, ...queryParams}, verb: 'push'})
      })
    } else {
      openInNewTab(this.urlForWorkspace({workspace, queryParams}, true))
    }
  }

  /*private _generateNavUrlForObject = (product) => {
    if (!site.seasonal) {
      const {plan, subCategories, collection} = product

      return removeUndefinedsNested({
        vlp_plans: [plan?.id].filter(_.identity),
        vlp_folders: subCategories ? stringify([subCategories.map(f => f.id).slice().filter(_.identity)]) : undefined,
        vlp_collections: [collection?.id].filter(_.identity)
      })
    }
  }*/

  get stores() { return globalStores }
  urlForObject = (o: any, p?: ICatalogItemDetailsPathElement[], c?: CatalogStore) => {
    const {stores: {flexLayout: {activeCatalogRoot}}} = this,
      catalog = c ?? activeCatalogRoot?.catalog ?? globalStores?.catalog,
      id = catalog.idFor(o),
      path = p.length ? Base64.encode(JSON.stringify(p)) : undefined,
      isLocalHost = location.hostname === 'localhost'

    return `${window.origin}/${slugs.itemDetails}?path=${path}&ws=${c.index}&itemId=${id}${isLocalHost ? location.search.replace('?', '&') : ''}`
  }

  urlStringWithQueryParamsOnly = (qp: DashboardQueryParams) => {
    return `${window.origin}?${utils.stringifyQs(qp)}`
  }

  urlForWorkspace({workspace, itemId, queryParams}: { workspace: Partial<WorkspaceConfig>; itemId?: string, queryParams?: any }, asString: true): string
  urlForWorkspace({workspace, itemId, queryParams}: { workspace: Partial<WorkspaceConfig>; itemId?: string, queryParams?: any }, asString = true) {
    const urlRaw = nav.urlFor({
      workspace,
      queryParams: {
        ...nav.defaultQueryParam,
        ...(!itemId ? {} : stores.sidebar.queryParamsFor({right: {open: true, path: [{where: [{value: itemId}]}], autoFocusDetailItem: true}})),
        ...(queryParams ?? {})
      }
    })

    return asString === true ? nav.urlString(urlRaw) : urlRaw
  }

  queryParamsFor = ({itemId, queryParams}: { itemId?: string, queryParams?: any }) => {
    return {
      ...nav.defaultQueryParam,
      ...(!itemId ? {} : stores.sidebar.queryParamsFor({right: {open: true, path: [{where: [{value: itemId}]}], autoFocusDetailItem: true}})),
      ...(queryParams ?? {})
    }
  }

  @computed get homepage() {
    return '/'
  }

  @computed get defaultQueryParam() {
    const {stores: {ui}} = this
    return {
      ...deployments.queryParams,
    }
  }

  slugForWorkspace = (ws: Partial<WorkspaceConfig>) => {
    return slugify((ws?.slug ?? ws?.title ?? ws?._id ?? 'default').toString(), {lower: true})
  }

  /// region Update Page URL

  lastQueryParams = null
  setLastQueryParams = (withoutUndefineds) => {
    this.lastQueryParams = withoutUndefineds
  }

  // @computed.struct get nextPageQueryParams() {
  //   const {
  //     catalog
  //   } = stores
  //
  //   let r: DashboardQueryParams = {
  //     qr: stores.catalog.dialogs.showQrCodes ? null : undefined,
  //     ...(site?.queryParams ?? {}),
  //     //search: site.searchText,
  //     ...(catalog?.queryParams ?? {})
  //   }
  //
  //   return r
  // }

  /// endregion

  @computed get isItemDetailsPage() {
    return this.location.pathname.startsWith(`/${slugs.itemDetails}`)
  }

  getHistoryStateFromLockr = () : {search: string, pathname: string} => {
    const search = lockr.get(uiConstants.REDIRECT_SEARCH) as string,
      pathname = lockr.get(uiConstants.REDIRECT_PATHNAME) as string
    return {
      search, pathname
    }
  }

  setHistoryState = () => {
    if (!location.pathname.startsWith('/logout') && !location.pathname.startsWith('/login')) {
      lockr.set(uiConstants.REDIRECT_SEARCH, location.search)
      lockr.set(uiConstants.REDIRECT_PATHNAME, location.pathname)
    }
  }

  removeLockrHistory = () => {
    lockr.rm(uiConstants.REDIRECT_SEARCH)
    lockr.rm(uiConstants.REDIRECT_PATHNAME)
  }
}

export const nav = new NavStore()


// WEBPACK FOOTER //
// ./src/stores/nav/NavStore.ts